// Pure goal-health classifier — THE single source of truth for the goal
// reconciliation rule. Shared by the MCP engine
// (packages/excellent-mcp/src/goal-reconcile.ts, which auto-closes goals) and
// the UI badge (packages/ui/src/blocks/agents/goal-health.ts). No DB, no I/O —
// counts in, bucket out — so both runtimes import it instead of forking the
// logic (which would let the UI badge disagree with what the background
// reconciler actually does).
//
// A goal is *accomplished* when it has ≥1 linked issue and every linked issue
// is terminal (done|cancelled) with at least one actually `done`. Because each
// issue itself passed the ship/verify gate, such a goal is transitively
// verified — closing it spends no extra agent budget.
//
// That count is NOT sufficient on its own. `goalAutoCloseBlockReason` is the
// second half of the rule: a strategic / human-owned goal, or a goal that
// declares done-criteria the pass cannot evaluate, is never auto-closed
// (#46043). `ready` from the counts still means "linked set is terminal";
// it does not mean "close this".
export function classifyGoalHealth(c) {
    if (c.task_count === 0)
        return "needs-plan";
    const open = c.task_count - c.done_count - c.cancelled_count;
    if (open > 0)
        return "in-progress";
    // Every issue is terminal at this point.
    if (c.done_count > 0)
        return "ready";
    return "needs-review"; // all cancelled — not an auto-close
}
/** Headings / markers that declare a done-predicate the linked-task pass
 *  cannot evaluate. Intentionally a declaration detector, not a scorer. */
const UNEVALUABLE_CRITERIA_RE = /program-done|done criteria|acceptance criteria|definition of done|do not auto-close|humans own the checkbox/i;
/** True when the structured `criteria` column holds at least one item. */
export function criteriaColumnIsPopulated(raw) {
    if (raw == null)
        return false;
    const s = String(raw).trim();
    if (!s || s === "null" || s === "[]")
        return false;
    try {
        const parsed = JSON.parse(s);
        return Array.isArray(parsed) && parsed.length > 0;
    }
    catch {
        // Non-JSON but non-empty — treat as declared so we fail closed.
        return true;
    }
}
/** True when the goal declares done-criteria this pass cannot evaluate. */
export function goalDeclaresUnevaluableCriteria(description, criteria) {
    if (criteriaColumnIsPopulated(criteria))
        return true;
    return UNEVALUABLE_CRITERIA_RE.test(description ?? "");
}
/**
 * Why this goal must not be auto-closed, or null if the linked-task
 * predicate is allowed to fire. Shared by the per-task event handler
 * (`reconcileGoalForTask`) and the batch reconcile pass.
 *
 *  - `kind='strategic'` — human-owned; measured by key results, not tasks.
 *  - `owner_agent_id='human'` — the explicit human-owned sentinel (v133).
 *  - declared unevaluable criteria — refuse rather than score the prose.
 */
export function goalAutoCloseBlockReason(g) {
    if ((g.kind ?? "agentic") === "strategic")
        return "strategic-human-owned";
    if (g.owner_agent_id === "human")
        return "human-owned-sentinel";
    if (goalDeclaresUnevaluableCriteria(g.description, g.criteria)) {
        return "unevaluable-done-criteria";
    }
    return null;
}
